import React, { FormEvent, useContext, useState, ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Shield, Mail, Lock, ArrowRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { AuthContext } from '../../contexts/AuthContext';
import { ApiApi, CustomUserFromJSON } from 'api-client';
import { getApiConfiguration } from '../../api/utils';
import { GoogleOAuthProvider , GoogleLogin } from "@react-oauth/google";

const getClient = () => {
  return new ApiApi(getApiConfiguration());
};

const Login = () => {
  const { setUserDetails } = useContext(AuthContext);
  const [searchParams] = useSearchParams();
  const isTrial = searchParams.get('trial') === 'true';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState<ReactNode>('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setLoginError('');

    const client = getClient();
    const credentials = { email, password };

    try {
      const data = await client.apiAuthLoginCreate({ login: credentials });
      
      if (data.status === "success" && data.jwt) {
        setUserDetails(data.jwt);
        navigate('/');
      } else if (data.status === "otp_required" && data.tempOtpToken) {
        localStorage.setItem('tempOtpToken', data.tempOtpToken);
        navigate('/2fa');
      }
    } catch (error: any) {
      const errorData = await error.response?.json();
      console.error(errorData);
      
      if (errorData?.non_field_errors) {
        if (errorData.non_field_errors.includes("E-mail is not verified.")) {
          setLoginError(
            <>
              Please verify your email address before logging in.{' '}
              <Link to="/resend-verification" className="text-blue-500 hover:text-blue-400 underline">
                Resend verification email
              </Link>
            </>
          );
        } else if (errorData.non_field_errors.includes("Unable to log in with provided credentials.")) {
          setLoginError("Invalid email or password. Please try again.");
        } else {
          setLoginError(errorData.non_field_errors[0]);
        }
      } else {
        setLoginError("There was a problem logging in. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // Implement Google login
    console.log('Google login');
  };

  const handleOktaLogin = () => {
    // Implement Okta login
    console.log('Okta login');
  };

  return (
    <div className="min-h-screen bg-[#0A0F1C] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="bg-[#0F1629] rounded-xl border border-gray-800/50 p-8">
          {/* Header */}
          <div className="flex items-center justify-center mb-8">
            <img src="/logo icon white.svg" alt="Flawtrack Logo" className="h-8 w-8" />
            <h1 className="text-2xl font-bold text-white ml-3">Flawtrack</h1>
          </div>

          {/* Title */}
          <div className="text-center mb-8">
            <h2 className="text-xl font-semibold text-white">{isTrial ? 'Start Your Free Trial' : 'Welcome back'}</h2>
            <p className="text-sm text-gray-400 mt-1">{isTrial ? 'Sign in to begin your trial' : 'Sign in to your account'}</p>
          </div>

          {/* SSO Buttons */}
          <div className="space-y-3 mb-6">
            {/* <button
              onClick={handleGoogleLogin}
              disabled={isGoogleLoading}
              className="w-full flex items-center justify-center space-x-3 px-4 py-3 bg-white hover:bg-gray-50 text-gray-900 rounded-lg transition-colors disabled:opacity-70"
            >
              <img src="https://www.google.com/favicon.ico" alt="Google" className="w-5 h-5" />
              <span className="font-medium">{isGoogleLoading ? 'Signing in...' : 'Continue with Google'}</span>
            </button> */}
            <GoogleOAuthProvider clientId={import.meta.env.VITE_APP_GOOGLE_CLIENT_ID}>
              <GoogleLogin
                auto_select={false}
                onSuccess={async (credentialResponse) => {
                  try {
                    console.log(credentialResponse);
                    if (!credentialResponse.credential) {
                      throw new Error('No credential received from Google');
                    }
                    const response = await getClient().apiAuthApiSocialGoogleCreate({
                      socialLoginRequest: {
                        accessToken: credentialResponse.credential,
                        idToken: credentialResponse.credential,
                        // code: credentialResponse.clientId
                      }
                    });
                    console.log(response);
                    // Set the JWT token in the auth context
                    if (response && response?.access) {
                      setUserDetails({
                        user: CustomUserFromJSON(response.user),
                        access: response.access,
                        refresh: response.refresh
                      });
                      navigate("/");
                    } else {
                      console.error("Invalid response format from Google login");
                      setLoginError("There was a problem with Google login. Please try again.");
                    }
                  } catch(error) {
                    console.error(error);
                    setLoginError("Failed to authenticate with Google. Please try again.");
                  }
                }}
                onError={() => {
                  console.log('Login Failed');
                  setLoginError("Google login failed. Please try again.");
                }}
                useOneTap={false}
                theme="filled_blue"
                size="large"
                shape="rectangular"
                text="continue_with"
                locale="en"
                width={382}
                containerProps={{ 
                  style: { 
                    width: '100%', 
                    display: 'flex', 
                    justifyContent: 'center',
                    maxWidth: '100%',
                    height: "48px"
                  } 
                }}
              />
            </GoogleOAuthProvider>

            <button
              onClick={handleOktaLogin}
              className="w-full flex items-center justify-center space-x-3 px-4 py-3 bg-[#007DC1] hover:bg-[#006BA9] text-white rounded-lg transition-colors"
            >
              <img
                src="https://www.okta.com/sites/default/files/Okta_Logo_BrightBlue_Medium.png"
                alt="Okta"
                className="w-5 h-5 object-contain"
              />
              <span className="font-medium">Sign in with Okta</span>
            </button>
          </div>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-800"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-[#0F1629] text-gray-500">Or continue with</span>
            </div>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <label className="text-sm text-gray-400">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-500" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-gray-800/30 border border-gray-800 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                  placeholder="Enter your email"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-sm text-gray-400">Password</label>
                <Link to="/forgot-password" className="text-sm text-blue-500 hover:text-blue-400">
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-500" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-gray-800/30 border border-gray-800 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                  placeholder="Enter your password"
                  required
                />
              </div>
            </div>

            {loginError && (
              <div className="text-sm text-red-500 bg-red-500/10 px-4 py-2 rounded-lg">
                {loginError}
              </div>
            )}

            <Button 
              className="w-full hover:opacity-50" 
              type="submit" 
              disabled={isLoading}
            >
              {isLoading ? 'Signing in...' : 'Sign in'}
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </form>

          {/* Footer */}
          <div className="text-center mt-6 space-y-4">
            <p className="text-sm text-gray-400">
              Don't have an account?{' '}
              <Link to="/signup" className="text-blue-500 hover:text-blue-400">
                Sign up
              </Link>
            </p>
            <p className="text-sm text-gray-400">
              Or{' '}
              <Link to="/magic-link" className="text-blue-500 hover:text-blue-400">
                Login with Magic Link
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;